<template>
  <CRow>
    <CCol lg="12">
      <h1 class="main-header mb-1" v-translate translate-context="accounting">
        Transactions
      </h1>
      <p class="text-help-dark" v-translate translate-context="accounting">Register of accounting transactions</p>

      <CCard>
        <CCardHeader class="border-0 ml-3">
          <div class="d-flex align-items-center font-weight-medium">
            <CIcon name="cipShoppingBag01" size="custom-size" :width="18" />
            <span class="mx-1" v-translate translate-context="accounting">All transactions</span>
            <span>({{ transactions.pagination.total|number }})</span>
          </div>

          <div class="card-header-actions">
            <PButton
                class="btn btn-sm btn-primary mr-3"
                @click="onExport"
                :loading="exporting"
                :loading-text="$gettext('Generating file...')"
                spinner-color="secondary"
                :disabled="!transactions.results || transactions.results.length <= 0"
                v-if="hasScopePerm('accounting.transactions.export')"
            >
              <CIcon width="30" name="cipFileDownload02" class="mr-2"/>
              <translate translate-context="accounting.actions">Export</translate>
            </PButton>
          </div>
        </CCardHeader>

        <CCardBody class="px-0">
          <AjaxTable
              v-if="isReady"
              ref="transactionsTable"
              :fields="fields"
              :data="transactions"
              :fetch="fetchList"
              table-name="accounting-transactions"
              :context-key="contextKey"
              :sync-url="true"
              class="custom-table custom-table--clean custom-table--has-calendar mx-4"
          >
            <template #effective_date="{item}">
              <td>
                {{ item.effective_date|formatDateTime('L') }}
              </td>
            </template>

            <template #transaction_type="{item}">
              <td>
                <CBadge class="badge-outline" :color="getTypeColor(item.transaction_type)">
                  <div class="d-flex align-items-center">
                    <CIcon :name="getTypeIcon(item.transaction_type)" size="custom-size" :width="14" class="mr-2"  />
                    <span class="font-weight-normal">{{ getTypeLabel(item.transaction_type) }}</span>
                  </div>
                </CBadge>
              </td>
            </template>

            <template #event_name="{item}">
              <td>
                <template v-if="item.pica_services && item.pica_services.length">
                  <template v-for="(ps, idx) in item.pica_services">
                    <router-link
                      :key="ps.id"
                      :to="{ name: 'picaServiceRedirect', params: { picaServiceId: ps.id } }"
                      class="reference-link"
                      @click.native.stop
                    >{{ ps.event_name }}</router-link><span v-if="idx < item.pica_services.length - 1" :key="'sep-ps-' + idx">, </span>
                  </template>
                </template>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #payment_method="{item}">
              <td>
                <span v-if="item.payment_method">{{ item.payment_method }}</span>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #contract_manager="{item}">
              <td>
                <template v-if="item.contract_managers && item.contract_managers.length">
                  <template v-for="(cm, idx) in item.contract_managers">
                    <router-link
                      :key="cm.id"
                      :to="{ name: 'contract_manager.detail', params: { id: cm.id } }"
                      class="reference-link"
                      @click.native.stop
                    >{{ cm.name }}</router-link><span v-if="idx < item.contract_managers.length - 1" :key="'sep-cm-' + idx">, </span>
                  </template>
                </template>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #total_amount="{item}">
              <td class="text-right">
                <span>{{item.total_amount|currency(item.currency)}}</span>
              </td>
            </template>

            <template #payer="{item}">
              <td>
                <div v-if="item.payer">
                  <div>{{ item.payer.name || item.payer.email }}</div>
                  <small class="text-muted" v-if="item.payer.name && item.payer.email">
                    {{ item.payer.email }}
                  </small>
                </div>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #reference="{item}">
              <td>
               <CLink @click="navigateToSource(item)" class="link--order">
                <span v-if="item.context && item.context.order_number">
                  {{ item.context.order_number }}
                </span>
                <span v-else class="text-muted">
                  #{{ item.source_id }}
                </span>
               </CLink>
              </td>
            </template>

            <template #actions="{item}">
              <td class="text-right">
                 <CButton color="dark"
                         variant="outline"
                         class="btn-circle btn-circle--sm d-inline-flex align-items-center justify-content-center"
                         size="sm"
                         @click="navigateToSource(item)">
                  <CIcon name="cipArrowRight"/>
                </CButton>
              </td>
            </template>
          </AjaxTable>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>

</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PButton from '@/components/PButton.vue'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import {
  TRANSACTION_TYPES,
  TRANSACTION_TYPE_LABELS,
  TRANSACTION_TYPE_COLORS,
  TRANSACTION_TYPE_OPTIONS,
  TRANSACTION_TYPE_ICON,
  getDetailRouteParams
} from './constants'
import {timeZone} from "../../utils/date";
import ConfigScope from "@/domain/core/constant/configScope";

export default {
  name: 'ListTransaction',
  components: {
    PButton,
    AjaxTable
  },
  props: {
    scopeType: {
      type: String,
      required: true,
      validator: v => ['global', 'contract-manager', 'pica-service'].includes(v)
    },
    scopeId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isReady: false,
      exporting: false
    }
  },
  async mounted() {
    // Set scope in store before AjaxTable mounts
    await this.setScope(this.scope)

    await Promise.all([
      this.fetchScopedConfig(),
      this.fetchPaymentMethods(null)
    ])

    // Now AjaxTable can mount and fetch
    this.isReady = true
  },
  computed: {
    timeZone() {
      return timeZone
    },
    ...mapGetters('accounting', ['transactions']),
    ...mapGetters('core', { corePaymentMethods: 'paymentMethods' }),

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    contextKey() {
      if (this.scopeType === 'global') return 'global'
      return `${this.scopeType}-${this.scopeId}`
    },

    transactionTypeOptions() {
      return [
        { value: '', label: this.$pgettext('accounting.list', 'Scegli') },
        ...TRANSACTION_TYPE_OPTIONS
      ]
    },

    paymentMethodOptions() {
      return [
        { value: '', label: this.$pgettext('accounting.list', 'Scegli') },
        ...this.corePaymentMethods.map(m => ({ value: m.name, label: m.label }))
      ]
    },

    fields() {
      const cols = [
        {
          key: 'effective_date',
          label: this.$pgettext('accounting.list', 'Data'),
          _style: 'width: 140px;',
          sorter: true,
          dateRangeFrom: 'date_from',
          dateRangeTo: 'date_to'
        },
        {
          key: 'reference',
          label: this.$pgettext('accounting.list', 'Reference'),
          _style: 'width: 170px;',
          sorter: false,
          filter: false
        },
        {
          key: 'event_name',
          label: this.$pgettext('accounting.list', 'Event'),
          sorter: false
        },
        {
          key: 'payment_method',
          label: this.$pgettext('accounting.list', 'Payment'),
          _style: 'width: 130px;',
          sorter: false,
          options: this.paymentMethodOptions
        },
        {
          key: 'total_amount',
          label: this.$pgettext('accounting.list', 'Amount'),
          _style: 'width: 130px;',
          sorter: true,
          filter: false
        },
      ]

      if (this.scopeType === 'global') {
        cols.push({
          key: 'contract_manager',
          label: this.$pgettext('accounting.list', 'Contract Manager'),
          sorter: false
        })
      }

      cols.push(
        {
          key: 'payer',
          label: this.$pgettext('accounting.list', 'Payer'),
          sorter: false
        },
        {
          key: 'transaction_type',
          label: this.$pgettext('accounting.list', 'Type'),
          _style: 'width: 140px;',
          sorter: false,
          options: this.transactionTypeOptions
        },
        {
          key: 'actions',
          label: '',
          _style: 'width: 80px;',
          sorter: false,
          filter: false
        }
      )

      return cols
    }
  },
  methods: {
    ...mapActions('accounting', [
      'setScope',
      'fetchTransactionList',
      'exportTransactions'
    ]),
    ...mapActions('login', ['fetchConfig']),
    ...mapActions('core', ['fetchPaymentMethods']),

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet, continue without it
      }
    },

    /**
     * Check permission with scope prefix
     */
    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    /**
     * Fetch function passed to AjaxTable
     */
    async fetchList({ filters, pagination, sorting }) {
      await this.fetchTransactionList({
        filters,
        pagination,
        sorting
      })
    },

    async onExport() {
      this.exporting = true
      try {
        const filters = this.$refs.transactionsTable
          ? this.$refs.transactionsTable.getFilters()
          : {}

        if (!filters.date_from) {
          const oneYearAgo = new Date()
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
          filters.date_from = oneYearAgo.toISOString().split('T')[0]
        }
        if (!filters.date_to) {
          filters.date_to = new Date().toISOString().split('T')[0]
        }

        await this.exportTransactions(filters)
      } finally {
        this.exporting = false
      }
    },

    navigateToSource(item) {
      // For order transactions, navigate to the new OrderDetail page
      if (item.transaction_type === TRANSACTION_TYPES.ORDER) {
        this.$router.push({
          name: 'admin.accounting.order',
          params: { id: item.source_id }
        })
        return
      }
      const route = getDetailRouteParams(item.transaction_type, item.source_id, this.scope)
      if (route) {
        this.$router.push(route)
      }
    },

    getTypeLabel(type) {
      return TRANSACTION_TYPE_LABELS[type] || type
    },

    getTypeColor(type) {
      return TRANSACTION_TYPE_COLORS[type] || 'secondary'
    },

    getTypeIcon(type) {
      return TRANSACTION_TYPE_ICON[type] || 'cipInfo'
    }
  }
}
</script>

<style scoped>
.custom-table--clickable >>> tbody tr {
  cursor: pointer;
}
.custom-table--clickable >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
